<template>
	<div>
		<div class="cs-head">
			<div>
				<h1 class="cs-title">Reports</h1>
				<p class="cs-sub">How long quotes wait for finance, how often they come back, and where the discounts go. Below: custom lines from won quotes that can become SKUs.</p>
			</div>
		</div>

		<div v-if="measures" class="cards">
			<div class="kcard"><h6>Time with finance <span class="muted">business days, average</span></h6>
				<div class="d-kv"><span>Draft to sent to finance</span><b>{{ days(measures.business_days.draft_to_ready) }}</b></div>
				<div class="d-kv"><span>Sent to finance to decided</span><b>{{ days(measures.business_days.ready_to_decision) }}</b></div>
				<div class="d-kv"><span>Decided to sent to the client</span><b>{{ days(measures.business_days.decision_to_sent) }}</b></div>
				<div class="d-kv"><span>Waiting for finance over 2 days, now</span><b :class="measures.over_clock ? 'lost' : ''">{{ measures.over_clock }}</b></div>
			</div>
			<div class="kcard"><h6>Quotes</h6>
				<div class="d-kv"><span>All quotes</span><b>{{ measures.quotes }}</b></div>
				<div class="d-kv"><span>Returned to sales for more detail</span><b>{{ measures.returned }}</b></div>
				<div class="d-kv"><span>Lines below a floor</span><b>{{ measures.below_floor_lines }}</b></div>
				<div class="d-kv"><span>Hours spent coordinating, per closed quote</span><b>{{ measures.hours_at_close_avg === null ? '—' : measures.hours_at_close_avg }}</b></div>
			</div>
			<div class="kcard"><h6>Discounts</h6>
				<div class="d-kv"><span>Quotes that are not standard</span><b>{{ measures.by_category.non_standard || 0 }}</b></div>
				<div class="d-kv"><span>Discounts on a whole quote</span><b>{{ measures.quote_discounts }}</b></div>
				<div class="d-kv"><span>Average discount, discounted quotes</span><b>{{ measures.effective_discount_bp_avg === null ? '—' : (measures.effective_discount_bp_avg / 100).toFixed(1) + '%' }}</b></div>
			</div>
		</div>

		<div v-if="measures" class="cards" style="margin-top:12px">
			<div v-for="g in discountGroups" :key="g.title" class="kcard"><h6>{{ g.title }}</h6>
				<div v-for="(v, k) in g.rows" :key="k" class="d-kv"><span>{{ g.label(k) }}</span><b>{{ v.count }} · {{ money(v.cents) }}</b></div>
				<div v-if="!Object.keys(g.rows).length" class="muted">No discount yet.</div>
			</div>
		</div>

		<h6 class="bd-grp" style="margin-top:26px">Parallel run · the engine against the spreadsheets</h6>
		<p class="sub-hint" style="margin:2px 0 0">Every quote where the salesperson saved the spreadsheet price on the Send screen. Within 5% reads as close; above that, look at the lines together.</p>
		<div class="tbl-scroll" style="margin-top:8px">
			<table class="tbl nochk">
				<thead><tr><th class="sticky-l2">Quote</th><th>Salesperson</th><th class="r">Engine, a year</th><th class="r">Spreadsheet, a year</th><th class="r">Difference</th><th class="r">Engine, one-time</th><th class="r">Spreadsheet, one-time</th><th>Note</th></tr></thead>
				<tbody>
					<tr v-for="r in parallel" :key="r.id" class="rowable" @click="$router.push('/pricing/admin/quotes?open=' + r.id)">
						<td class="sticky-l2"><div class="acct"><span><span class="acct-name">{{ r.account }}</span><span class="sub mono">{{ r.number }}</span></span></div></td>
						<td>{{ r.owner || '—' }}</td>
						<td class="r num">{{ money(r.annual_net_cents) }}</td><td class="r num">{{ money(r.reference_annual_cents) }}</td>
						<td class="r num"><span :class="Math.abs(r.diffPct) <= 5 ? 'pe-cmp-good' : 'pe-cmp-bad'">{{ signedMoney(r.diff) }} · {{ signedPct(r.diffPct) }}</span></td>
						<td class="r num">{{ money(r.one_time_net_cents) }}</td><td class="r num">{{ r.reference_one_time_cents === null ? '—' : money(r.reference_one_time_cents) }}</td>
						<td class="wrap faint">{{ r.reference_note || '' }}</td>
					</tr>
					<tr v-if="!parallel.length" class="empty"><td colspan="8"><i class="fa-regular fa-folder-open"></i><div>No spreadsheet prices saved yet. Sales save them on the Send screen, under Parallel run.</div></td></tr>
				</tbody>
			</table>
		</div>

		<h6 class="bd-grp" style="margin-top:26px">Precedents · custom lines on won quotes</h6>
		<p class="sub-hint" style="margin:2px 0 0">Promote one and it becomes a SKU with a draft price in the list you pick. It reaches quotes when that draft is published.</p>
		<div class="tbl-scroll" style="margin-top:8px">
			<table class="tbl nochk">
				<thead><tr><th class="sticky-l2">Proposed SKU</th><th>From quote</th><th>Description</th><th class="r">Cost</th><th class="r">Floor</th><th class="r">Price</th><th>Approved</th><th>Expires</th><th class="end"></th></tr></thead>
				<tbody>
					<tr v-for="p in precedents" :key="p.id">
						<td class="sticky-l2"><div class="acct"><span class="avatar" :class="brandClass(p.brand ? p.brand.code : '')">{{ p.brand ? p.brand.code : '?' }}</span><span><span class="acct-name mono">{{ p.proposed_code || 'no code yet' }}</span><span class="sub">{{ p.brand ? p.brand.name : '' }} · {{ p.promoted_sku_id ? 'promoted' : 'proposed' }}</span></span></div></td>
						<td class="mono">{{ p.quote ? p.quote.number : '—' }}</td>
						<td class="wrap">{{ p.description }}</td>
						<td class="r num">{{ p.cost_cents === null ? 'not set' : money(p.cost_cents) }}</td>
						<td class="r num">{{ p.floor_cents === null ? 'not set' : money(p.floor_cents) }}</td>
						<td class="r num">{{ p.price_cents === null ? '—' : money(p.price_cents) }}</td>
						<td>{{ p.approved_at ? 'finance, ' + fmtDate(p.approved_at) : '—' }}</td>
						<td>{{ p.expires_at ? fmtDate(p.expires_at) : 'not set' }}</td>
						<td class="end"><button v-if="!p.promoted_sku_id" class="pill sm" @click="promote(p)">Promote to SKU</button><span v-else class="yn yes">SKU</span></td>
					</tr>
					<tr v-if="!precedents.length" class="empty"><td colspan="9"><i class="fa-regular fa-folder-open"></i><div>No precedents yet. A won quote with a custom line lands here.</div></td></tr>
				</tbody>
			</table>
		</div>
	</div>
</template>

<script>
import restApi from '../../../api/index.js'
import { money, fmtDate, brandClass, brandName, errorText } from '../pe-util'

export default {
	name: 'PricingReports',
	data() { return { measures: null, precedents: [], quotes: [] } },
	computed: {
		parallel() {
			return this.quotes.filter(q => q.reference_annual_cents !== null && q.reference_annual_cents !== undefined).map(q => {
				const diff = q.annual_net_cents - q.reference_annual_cents
				return Object.assign({}, q, { diff, diffPct: q.reference_annual_cents ? Math.round(diff / q.reference_annual_cents * 1000) / 10 : 0 })
			}).sort((a, b) => Math.abs(b.diffPct) - Math.abs(a.diffPct))
		},
		discountGroups() {
			const m = this.measures
			return [{ title: 'Discounts by reason', rows: m.discounts_by_reason, label: k => k.replace(/_/g, ' ') }, { title: 'Discounts by salesperson', rows: m.discounts_by_owner, label: k => k }, { title: 'Discounts by brand', rows: m.discounts_by_brand, label: k => brandName(k) }]
		},
	},
	created() { this.load() },
	methods: {
		money, fmtDate, brandClass,
		days(v) { return v === null || v === undefined ? '—' : v },
		signedMoney(c) { return (c > 0 ? '+' : c < 0 ? '−' : '') + money(Math.abs(c)) },
		signedPct(v) { return (v > 0 ? '+' : v < 0 ? '−' : '') + Math.abs(v) + '%' },
		toast(title, icon = 'error') { this.$root.$emit('pe-notice', { tone: icon === 'success' ? 'ok' : (icon === 'info' ? 'info' : 'warn'), text: title }) },
		load() {
			Promise.all([restApi.get('/pricing/measures'), restApi.get('/pricing/precedents'), restApi.get('/pricing/quotes', { params: { perPage: 200 } })]).then(([m, p, q]) => { this.measures = m.data; this.precedents = p.data; this.quotes = q.data.data })
				.catch(err => this.toast(errorText(err, 'Could not load the reports')))
		},
		promote(p) {
			restApi.get('/pricing/schedules').then(({ data }) => {
				this.$root.$pePrompt({ title: 'Promote to SKU', fields: [{ key: 'code', label: 'SKU code', value: p.proposed_code || '', placeholder: 'TWK-SITE-ANNUAL' }, { key: 'schedule_id', label: 'Price list for the draft price', type: 'select', options: data.map(s => ({ value: s.id, label: s.name })) }], confirm: 'Promote' })
					.then(v => { if (!v) return; restApi.post('/pricing/precedents/' + p.id + '/promote', { code: String(v.code).trim().toUpperCase(), schedule_id: Number(v.schedule_id) }).then(() => { this.toast('SKU created with a draft price', 'success'); this.load() }).catch(err => this.toast(errorText(err, 'Promote failed'))) })
			})
		},
	},
}
</script>
